import { Box, Stack, TextField, Typography } from "@mui/material";
import {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

const UnitNumberTemplateForm = forwardRef(
  ({ onDataChange, initialData }, ref) => {
    const [unitPrefix, setUnitPrefix] = useState("");
    const [unitPrefixError, setUnitPrefixError] = useState(false);
    const [unitPrefixErrorMsg, setUnitPrefixErrorMsg] = useState("");
    const unitPrefixRef = useRef(null);
    const maxLength = 50;

    const [startingNumber, setStartingNumber] = useState(1);
    const [startingNumberError, setStartingNumberError] = useState(false);
    const [startingNumberErrorMsg, setStartingNumberErrorMsg] = useState("");
    const startingNumberRef = useRef(null);
    const maxStartNumberLength = 10;

    // Define form state for text inputs and select field
    const [formData, setFormData] = useState({
      unitPrefix: initialData?.unitPrefix || "",
      startingNumber: initialData?.startingNumber || "",
    });

    useEffect(() => {
      // Update formData when initialData changes (e.g., when editing)
      if (initialData) {
        setFormData(initialData);
      }
    }, [initialData]);

    // Handle text/select input changes & update formData state
    const handleChange = (e) => {
      const { name, value } = e.target;

      if (name === "unitPrefix") {
        setUnitPrefix(value);
        setUnitPrefixError(false);
        setUnitPrefixErrorMsg("");

        if (value.length >= maxLength) {
          setUnitPrefixErrorMsg(
            `This field cannot be more than ${maxLength} characters`
          );
        }
      }

      if (name === "startingNumber") {
        const numericValue = parseInt(value, 10) || 1;
        setStartingNumber(numericValue);
        setStartingNumberError(false);
        setStartingNumberErrorMsg("");

        if (numericValue.toString().length >= maxStartNumberLength) {
          setStartingNumberErrorMsg(
            `This field cannot be more than ${maxStartNumberLength} characters`
          );
        }
      }

      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));

      // Notify parent of data changes
      onDataChange({
        ...formData,
        [name]: value,
      });
    };

    // Validate all form fields on submit
    const validateUserInput = () => {
      // Validate startingNumber
      if (isNaN(parseInt(formData.startingNumber, 10))) {
        setStartingNumberError(true);
        setStartingNumberErrorMsg("This field is required");
        startingNumberRef.current.focus();
        return false;
      } else if (formData.startingNumber.length > maxStartNumberLength) {
        setStartingNumberError(true);
        setStartingNumberErrorMsg(
          `This field cannot be more than ${maxStartNumberLength} characters`
        );
        startingNumberRef.current.focus();
        return false; // Prevent form submission
      }

      // Validate unitPrefix Length
      if (formData.unitPrefix.length > maxLength) {
        setUnitPrefixError(true);
        setUnitPrefixErrorMsg(
          `This field cannot be more than ${maxLength} characters`
        );
        unitPrefixRef.current.focus();
        return false;
      } else {
        setUnitPrefixError(false);
        setUnitPrefixErrorMsg("");
      }

      return true;
    };

    // Expose the validate method to the parent via ref
    useImperativeHandle(ref, () => ({
      validateUserInput,
      // To fetch all form data from UnitForm:
      getFormData: () => ({
        ...formData,
        // unitPrefix,
        // startingNumber: parseInt(startingNumber, 10),
      }),
    }));

    return (
      <>
        <Box sx={{ width: { xs: "75%", sm: 400 } }}>
          <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
            <TextField
              type="text"
              label="Unit Prefix"
              name="unitPrefix"
              value={formData.unitPrefix}
              inputRef={unitPrefixRef}
              onChange={handleChange}
              autoComplete="off"
              fullWidth
              slotProps={{
                htmlInput: {
                  maxLength: maxLength, // Set maxLength to 50 characters
                },
              }}
              error={unitPrefixError} // Show error if limit is reached
              helperText={
                <Typography
                  variant="body4"
                  sx={{ color: `${unitPrefixError && "error"}` }}
                >
                  {unitPrefixErrorMsg}
                </Typography>
              }
            />
            <TextField
              label="Starting Number"
              type="number"
              name="startingNumber"
              inputRef={startingNumberRef}
              value={formData.startingNumber}
              onChange={handleChange}
              error={startingNumberError}
              helperText={
                <Typography
                  variant="body4"
                  sx={{ color: `${startingNumberError && "error"}` }}
                >
                  {startingNumberErrorMsg}
                </Typography>
              }
              autoComplete="off"
              fullWidth
              required
              slotProps={{
                htmlInput: {
                  min: 1,
                  onKeyDown: (e) => {
                    // Prevent "e", "E", "+", and "-" from being entered
                    if (["e", "E", "+", "-"].includes(e.key)) {
                      e.preventDefault();
                    }
                  },
                },
              }}
            />
          </Stack>
          <Box sx={{ width: { xs: "75%", sm: 400 } }}>
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Example numbers:{" "}
              {Array.from(
                { length: 3 },
                (_, i) =>
                  `${formData.unitPrefix}${
                    parseInt(formData.startingNumber, 10) + i
                  }`
              ).join(", ")}
            </Typography>
          </Box>
        </Box>
      </>
    );
  }
);

export default UnitNumberTemplateForm;
